// Лонч: чистые функции. Доска (дорожка → треки → подряды), показанный статус задачи (blocked не хранится,
// а выводится из связей: задача стоит, пока хоть один её блокер не done) и что записать по каждому действию.
import type { Change, Row } from '@core/store/model';
import { addDays, daysBetween, type ISODate } from '@core/dates';
import { assignSubRows } from '../roadmap/shared/geometry';
import { nextPosition } from '../roadmap/shared/planner';

export type Launch = Row<'launch'>;
export type Swimlane = Row<'launch_swimlane'>;
export type Track = Row<'launch_track'>;
export type LaunchTask = Row<'launch_task'>;
export type Link = Row<'launch_link'>;

/** Статусы, которые хранятся (check в 0018 — источник истины); порядок — как в карточке. */
export const LAUNCH_STATUSES = ['queued', 'in_progress', 'done', 'at_risk', 'release'] as const;
export type LaunchStatus = (typeof LAUNCH_STATUSES)[number];
export type ShownStatus = LaunchStatus | 'blocked';

/** Палитра старого Launch View — Виктория узнаёт доску; glyph в названии — состояние не только цветом. Порядок — легенда. */
export const STATUS_LOOK: Record<ShownStatus, { label: string; bg: string; border: string; text: string; glyph?: string }> = {
  queued:      { label: 'Queued',      bg: '#f3f4f6', border: '#6b7280', text: '#374151' },
  in_progress: { label: 'In progress', bg: '#dbeafe', border: '#60a5fa', text: '#1d4ed8' },
  done:        { label: 'Done',        bg: '#dcfce7', border: '#4ade80', text: '#15803d' },
  blocked:     { label: 'Blocked',     bg: '#fee2e2', border: '#f87171', text: '#b91c1c', glyph: '⛔' },
  at_risk:     { label: 'At risk',     bg: '#fef3c7', border: '#fbbf24', text: '#92400e', glyph: '!' },
  release:     { label: 'Release',     bg: '#f3e8ff', border: '#a855f7', text: '#7e22ce', glyph: '⚑' },
};
export const SHOWN_STATUSES = Object.keys(STATUS_LOOK) as ShownStatus[];

export const byPosition = <T extends { position: number; name: string }>(a: T, b: T) => a.position - b.position || a.name.localeCompare(b.name);

// ---------------------------------------------------------------- статус

/** Невыполненные блокеры каждой задачи: считается один раз на доску, строки читают по id. */
export function openBlockersByTask(links: Iterable<Link>, tasks: ReadonlyMap<string, LaunchTask>): Map<string, LaunchTask[]> {
  const out = new Map<string, LaunchTask[]>();
  for (const l of links) {
    const b = tasks.get(l.blocker_id);
    if (!b || b.status === 'done') continue;
    out.set(l.blocked_id, [...(out.get(l.blocked_id) ?? []), b]);
  }
  return out;
}

/** Что показывать: done — всегда done; иначе blocked, если есть невыполненный блокер; иначе свой статус. */
export function statusOf(task: LaunchTask, openBlockers: ReadonlyMap<string, LaunchTask[]>): ShownStatus {
  if (task.status === 'done') return 'done';
  if ((openBlockers.get(task.id)?.length ?? 0) > 0) return 'blocked';
  return task.status as LaunchStatus;
}

/** Невыполненные среди выбранных в карточке блокеров (по драфту, не по связям). */
export const openBlockersAmong = (ids: Iterable<string>, byId: ReadonlyMap<string, LaunchTask>): LaunchTask[] =>
  [...ids].map((id) => byId.get(id)).filter((t): t is LaunchTask => !!t && t.status !== 'done');

/** Лонч трека: трек → дорожка → лонч; null, если трек или дорожка уже удалены. Release Plan строит из неё launchOf. */
export function launchIdOfTrack(trackId: string, tracks: ReadonlyMap<string, Track>, swimlanes: ReadonlyMap<string, Swimlane>): string | null {
  const track = tracks.get(trackId);
  const lane = track ? swimlanes.get(track.swimlane_id) : undefined;
  return lane?.launch_id ?? null;
}

// ---------------------------------------------------------------- доска

export type LaunchRow =
  | { key: string; kind: 'swimlane'; id: string; label: string; color: string }
  | { key: string; kind: 'track'; id: string; swimlaneId: string; label: string; tasks: LaunchTask[]; subRow: Map<string, number>; subRows: number };

export type Preview = { kind: 'move'; track_id: string; start_date: ISODate; end_date: ISODate } | { kind: 'resize'; start_date: ISODate; end_date: ISODate };
export type Hint = { track_id: string; start_date: ISODate; end_date: ISODate };

export type Board = { rows: LaunchRow[]; lanes: Swimlane[]; tracks: Track[]; trackOrder: string[]; tasks: LaunchTask[] };

/** Доска одного лонча: дорожки по порядку, под каждой её треки (tracks — в порядке доски); задачи трека раскладываются в подряды. */
export function boardOf(launchId: string, swimlanes: Iterable<Swimlane>, tracks: Iterable<Track>, tasks: Iterable<LaunchTask>): Board {
  const lanes = [...swimlanes].filter((s) => s.launch_id === launchId).sort(byPosition);
  const laneIds = new Set(lanes.map((s) => s.id));
  const trackList = [...tracks].filter((t) => laneIds.has(t.swimlane_id)).sort(byPosition);
  const byTrack = new Map<string, LaunchTask[]>();
  for (const t of tasks) byTrack.set(t.track_id, [...(byTrack.get(t.track_id) ?? []), t]);
  const rows: LaunchRow[] = [];
  const boardTracks: Track[] = [];
  const boardTasks: LaunchTask[] = [];
  for (const s of lanes) {
    rows.push({ key: `s:${s.id}`, kind: 'swimlane', id: s.id, label: s.name, color: s.color });
    for (const tr of trackList) {
      if (tr.swimlane_id !== s.id) continue;
      const ts = byTrack.get(tr.id) ?? [];
      const subRow = assignSubRows(ts);
      rows.push({ key: `t:${tr.id}`, kind: 'track', id: tr.id, swimlaneId: s.id, label: tr.name, tasks: ts, subRow, subRows: Math.max(1, ...[...subRow.values()].map((r) => r + 1)) });
      boardTracks.push(tr);
      boardTasks.push(...ts);
    }
  }
  return { rows, lanes, tracks: boardTracks, trackOrder: boardTracks.map((t) => t.id), tasks: boardTasks };
}

// ---------------------------------------------------------------- что записать: задачи

/** Сдвиг задачи на день (и в другой трек, если бросили туда): длина та же. */
export function planLaunchMove(t: LaunchTask, to: { track_id: string; start_date: ISODate }): Change[] {
  const end = addDays(to.start_date, daysBetween(t.start_date, t.end_date));
  if (to.track_id === t.track_id && to.start_date === t.start_date) return [];
  return [{ table: 'launch_task', id: t.id, set: { track_id: to.track_id, start_date: to.start_date, end_date: end }, expect_updated_at: t.updated_at }];
}

/** Новый край; задача не короче дня. */
export function planLaunchResize(t: LaunchTask, side: 'start' | 'end', date: ISODate): Change[] {
  const start = side === 'start' ? (date > t.end_date ? t.end_date : date) : t.start_date;
  const end = side === 'end' ? (date < t.start_date ? t.start_date : date) : t.end_date;
  if (start === t.start_date && end === t.end_date) return [];
  return [{ table: 'launch_task', id: t.id, set: { start_date: start, end_date: end }, expect_updated_at: t.updated_at }];
}

/** Групповой сдвиг по дням: у каждой задачи свои даты, трек не меняется. */
export function planLaunchGroupMove(tasks: LaunchTask[], days: number): Change[] {
  if (days === 0) return [];
  return tasks.map((t) => ({ table: 'launch_task' as const, id: t.id, set: { start_date: addDays(t.start_date, days), end_date: addDays(t.end_date, days) }, expect_updated_at: t.updated_at }));
}

/** Копия сразу после задачи, той же длины, в очереди. */
export function planLaunchDuplicate(t: LaunchTask, newId: string): Change[] {
  const len = daysBetween(t.start_date, t.end_date);
  return [{ table: 'launch_task', insert: { id: newId, track_id: t.track_id, title: t.title, start_date: addDays(t.end_date, 1), end_date: addDays(t.end_date, 1 + len), status: 'queued', notes: t.notes } }];
}

/** Только поля, отличающиеся от живой строки, — чтобы не писать и не журналировать то же самое. */
function changedFields(live: object, fields: Record<string, unknown>): Record<string, unknown> {
  const set: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) if ((live as Record<string, unknown>)[k] !== v) set[k] = v;
  return set;
}

export type LaunchTaskDraft = { id: string | null; track_id: string; title: string; start_date: ISODate; end_date: ISODate; status: LaunchStatus; notes: string; blockedBy: string[] };

export const LAUNCH_TASK_DAYS = 3; // новая задача лонча — три дня от дня под курсором
export const newLaunchTaskDraft = (track_id: string, start: ISODate): LaunchTaskDraft => ({ id: null, track_id, title: '', start_date: start, end_date: addDays(start, LAUNCH_TASK_DAYS - 1), status: 'queued', notes: '', blockedBy: [] });
/** Контур новой задачи под курсором — те же даты, что получит созданная. */
export const hintAt = (track_id: string, date: ISODate): Hint => ({ track_id, start_date: date, end_date: addDays(date, LAUNCH_TASK_DAYS - 1) });
export const launchTaskDraftOf = (t: LaunchTask, links: Iterable<Link> = []): LaunchTaskDraft => ({ id: t.id, track_id: t.track_id, title: t.title, start_date: t.start_date, end_date: t.end_date, status: t.status as LaunchStatus, notes: t.notes ?? '', blockedBy: [...links].filter((l) => l.blocked_id === t.id).map((l) => l.blocker_id) });

/** Карточка: только изменившиеся поля с expect_updated_at живой строки; новая — insert с готовым id. */
export function planLaunchTaskSave(d: LaunchTaskDraft, live: LaunchTask | undefined, newId: () => string): { taskId: string; changes: Change[] } | { error: string } {
  if (!d.title.trim()) return { error: 'Title is required' };
  if (d.end_date < d.start_date) return { error: 'End date is before the start' };
  const fields = { track_id: d.track_id, title: d.title.trim(), start_date: d.start_date, end_date: d.end_date, status: d.status, notes: d.notes.trim() || null };
  if (!d.id) { const id = newId(); return { taskId: id, changes: [{ table: 'launch_task', insert: { id, ...fields } }] }; }
  if (!live) return { error: 'This task was deleted' };
  const set = changedFields(live, fields);
  return { taskId: d.id, changes: Object.keys(set).length ? [{ table: 'launch_task', id: d.id, set, expect_updated_at: live.updated_at }] : [] };
}

/**
 * Кого можно выбрать блокером в карточке: все задачи лонча минус сама, минус уже выбранные, минус те, кого эта
 * задача блокирует хоть через кого-то — цикл любой длины невозможен.
 */
export function blockerCandidates(taskId: string, blockedBy: string[], links: Iterable<Link>, tasks: LaunchTask[]): LaunchTask[] {
  const downstream = new Set<string>();
  const all = [...links];
  const queue = [taskId];
  for (let cur = queue.shift(); cur !== undefined; cur = queue.shift()) {
    for (const l of all) if (l.blocker_id === cur && !downstream.has(l.blocked_id)) { downstream.add(l.blocked_id); queue.push(l.blocked_id); }
  }
  return tasks.filter((t) => t.id !== taskId && !blockedBy.includes(t.id) && !downstream.has(t.id)).sort((a, b) => a.title.localeCompare(b.title));
}

/** Связи «Blocked by» задачи после карточки: недостающие добавить, лишние убрать. */
export function planLaunchLinks(taskId: string, wantBlockers: string[], links: Iterable<Link>): Change[] {
  const want = new Set(wantBlockers.filter((id) => id !== taskId));
  const changes: Change[] = [];
  for (const l of links) {
    if (l.blocked_id !== taskId) continue;
    if (want.has(l.blocker_id)) want.delete(l.blocker_id);
    else changes.push({ table: 'launch_link', id: l.id, delete: true });
  }
  for (const blocker of want) changes.push({ table: 'launch_link', insert: { blocker_id: blocker, blocked_id: taskId } });
  return changes;
}

// ---------------------------------------------------------------- что записать: лонч и строки

export type LaunchDraft = { id: string | null; name: string; start_date: ISODate; end_date: ISODate };
export const LAUNCH_DEFAULT_DAYS = 28; // новый лонч и доска без лонча — четыре недели от сегодня
export const newLaunchDraft = (start: ISODate): LaunchDraft => ({ id: null, name: '', start_date: start, end_date: addDays(start, LAUNCH_DEFAULT_DAYS - 1) });
export const launchDraftOf = (l: Launch): LaunchDraft => ({ id: l.id, name: l.name, start_date: l.start_date, end_date: l.end_date });

/** Карточка лонча: новый получает первую дорожку и трек, чтобы доска не была пустой; правка — только изменившиеся поля. */
export function planLaunchSave(d: LaunchDraft, launches: Iterable<Launch>, newId: () => string): { launchId: string; changes: Change[] } | { error: string } {
  if (!d.name.trim()) return { error: 'Name is required' };
  if (d.end_date < d.start_date) return { error: 'End date is before the start' };
  const fields = { name: d.name.trim(), start_date: d.start_date, end_date: d.end_date };
  const all = [...launches];
  if (!d.id) {
    const id = newId(), swimlaneId = newId();
    return { launchId: id, changes: [
      { table: 'launch', insert: { id, ...fields, position: nextPosition(all) } },
      { table: 'launch_swimlane', insert: { id: swimlaneId, launch_id: id, name: 'Swimlane', position: 0 } },
      { table: 'launch_track', insert: { swimlane_id: swimlaneId, name: 'Track', position: 0 } },
    ] };
  }
  const live = all.find((l) => l.id === d.id);
  if (!live) return { error: 'This launch was deleted' };
  const set = changedFields(live, fields);
  return { launchId: d.id, changes: Object.keys(set).length ? [{ table: 'launch', id: d.id, set, expect_updated_at: live.updated_at }] : [] };
}

export type RowTable = 'launch_swimlane' | 'launch_track';

/** Новая дорожка в конец лонча / новый трек в конец дорожки. */
export const planSwimlaneInsert = (launchId: string, lanes: Iterable<Swimlane>): Change[] =>
  [{ table: 'launch_swimlane', insert: { launch_id: launchId, name: 'New swimlane', position: nextPosition(lanes) } }];
export const planTrackInsert = (swimlaneId: string, tracks: Iterable<Track>): Change[] =>
  [{ table: 'launch_track', insert: { swimlane_id: swimlaneId, name: 'New track', position: nextPosition([...tracks].filter((t) => t.swimlane_id === swimlaneId)) } }];

/** Имя или цвет строки — с expect_updated_at живой строки. */
export const planRowSet = (table: RowTable, row: { id: string; updated_at: string }, set: { name?: string; color?: string }): Change[] =>
  [{ table, id: row.id, set, expect_updated_at: row.updated_at }];

/** Порядок строк после броска: fromId встаёт перед anchorId (null — в конец); трек может сменить дорожку. */
export function planRowsReorder(table: RowTable, rows: { id: string; position: number; updated_at: string; swimlane_id?: string }[], fromId: string, anchorId: string | null, swimlaneOfAnchor?: string | null): Change[] {
  const from = rows.find((r) => r.id === fromId);
  if (!from) return [];
  const targetLane = table === 'launch_track' ? (swimlaneOfAnchor ?? from.swimlane_id ?? null) : null;
  const inScope = rows.filter((r) => table === 'launch_swimlane' || r.swimlane_id === targetLane).filter((r) => r.id !== fromId)
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
  const at = anchorId ? inScope.findIndex((r) => r.id === anchorId) : -1;
  const ordered = at < 0 ? [...inScope, from] : [...inScope.slice(0, at), from, ...inScope.slice(at)];
  const changes: Change[] = [];
  ordered.forEach((r, i) => {
    const laneChange = r.id === fromId && targetLane && targetLane !== from.swimlane_id;
    if (r.position !== i || laneChange) changes.push({ table, id: r.id, set: { position: i, ...(laneChange ? { swimlane_id: targetLane } : {}) }, expect_updated_at: r.updated_at });
  });
  return changes;
}
